/**
 * Rodape de acoes de uma aba: estado do rascunho a esquerda, botoes a direita
 * e, quando ha erro, a LISTA do que falta com atalho para o campo.
 *
 * O botao principal fica DESABILITADO quando nao ha o que salvar: um "Salvar"
 * sempre clicavel ensina a pessoa a clicar por reflexo e esconde quando algo
 * de fato mudou. "Descartar" so aparece com alteracao pendente, pelo mesmo
 * motivo. O texto de estado vive num `role="status"` para o leitor de tela
 * anunciar "Salvo na previa" sem a pessoa procurar o selo.
 *
 * A lista de erros so aparece DEPOIS da primeira tentativa de salvar (quem
 * decide isso e a aba, via `bloqueado`): um formulario que abre vermelho
 * antes de qualquer tecla ensina a ignorar vermelho. Cada item e um botao que
 * leva o foco ao campo (`id` estavel passado pela aba via `htmlFor`), porque
 * numa aba longa "corrija os campos destacados" obriga a rolar procurando.
 */

import { Badge, Button, Icon } from '../../../components/ui';
import { formatCount } from '../../../lib/format';

export interface ErroDeCampo {
  /** `id` do controle, para focar. */
  readonly id: string;
  readonly campo: string;
  readonly mensagem: string;
}

export function focarCampo(id: string): void {
  const alvo = document.getElementById(id);
  if (alvo instanceof HTMLElement) {
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    alvo.focus({ preventScroll: true });
  }
}

export function BarraSalvar({
  dirty,
  saved,
  onSave,
  onDiscard,
  bloqueado = false,
  motivoBloqueio,
  erros = [],
  bordered = true,
  className = '',
}: {
  readonly dirty: boolean;
  readonly saved: boolean;
  readonly onSave: () => void;
  readonly onDiscard: () => void;
  /** Ha erro de validacao: o botao fica desabilitado e o motivo aparece ao lado. */
  readonly bloqueado?: boolean;
  readonly motivoBloqueio?: string;
  /** Erros por campo; com `bloqueado`, vira a lista com atalho de foco. */
  readonly erros?: readonly ErroDeCampo[];
  /** Linha fina acima da barra; `false` quando ela ja esta dentro de um Panel proprio. */
  readonly bordered?: boolean;
  readonly className?: string;
}): JSX.Element {
  const mostrarErros = dirty && bloqueado && erros.length > 0;
  return (
    <div className={`flex flex-col gap-3 ${bordered ? 'border-t border-line pt-4' : ''} ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="flex min-h-6 flex-wrap items-center gap-2 text-xs text-ink-subtle">
          {dirty ? (
            <Badge tone="warning" dot>
              Alterações não salvas
            </Badge>
          ) : saved ? (
            <Badge tone="good" icon="check">
              Salvo na prévia
            </Badge>
          ) : (
            'Nenhuma alteração pendente.'
          )}
          {dirty && bloqueado && motivoBloqueio !== undefined && !mostrarErros ? <span className="text-status-critical">{motivoBloqueio}</span> : null}
        </p>
        <div className="flex items-center gap-2">
          {dirty ? (
            <Button variant="ghost" onClick={onDiscard}>
              Descartar
            </Button>
          ) : null}
          <Button variant="primary" icon="check" onClick={onSave} disabled={!dirty || bloqueado}>
            Salvar na prévia
          </Button>
        </div>
      </div>

      {mostrarErros ? (
        <div role="alert" className="rounded-xl border border-status-critical/40 bg-status-critical/5 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-ink">
            <Icon name="alerta" size="sm" className="text-status-critical" />
            {erros.length === 1 ? 'Um campo precisa de atenção antes de salvar' : `${formatCount(erros.length)} campos precisam de atenção antes de salvar`}
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {erros.map((erro) => (
              <li key={erro.id}>
                <button
                  type="button"
                  onClick={() => focarCampo(erro.id)}
                  className="group inline-flex flex-wrap items-baseline gap-x-1.5 text-left text-xs text-ink-muted hover:text-ink"
                >
                  <span className="font-medium text-ink underline decoration-status-critical/60 underline-offset-2 group-hover:decoration-ink">{erro.campo}</span>
                  <span>— {erro.mensagem}</span>
                  <span className="text-ink-subtle">· ir para o campo</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
